import { createBrowserHistory } from "history";
import { FunctionComponent, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { MediumColoredIcons } from "@Components/molecules/product/MediumColoredIcons";
import { MediumProductCards } from "@Components/molecules/product/MediumProductCards";
import { ProductDescription } from "@Components/molecules/product/ProductDescription";
import { ProductInfos } from "@Components/molecules/product/ProductInfos";
import { HEADER_HEIGHT } from "@Components/organisms/Header/index";
import { useGlobalLoader } from "@Components/WithGlobalLoader";
import WithLocale from "@Components/WithLocale";
import useBannerKountriesHeight from "@Hooks/banner/useBannerKountriesHeight";
import { useBreakpoint } from "@Hooks/useBreakpoint";
import CheerzApiProvider from "@Providers/CheerzApiProvider";
import { ApiV2ErrorResponse } from "@Types/api/error";
import { Page, Section, Tile } from "@Types/pagify";
import { SettingsContent } from "@Types/settings";
import User from "@Types/user";
import { apiV2ErrorManager } from "@Utils/error";
import { getCsrfToken, setMeta } from "@Utils/meta";
import { convertPageFromApiToContent } from "@Utils/pagify";
import { addProductPageStructuredData } from "@Utils/pagify/structuredData";
import { convertSettingsFromApiToContent } from "@Utils/settings";
import { setDataLayerMandatoryPageTrackers } from "@Utils/track";
import "./style.scss";

const history = createBrowserHistory();

interface Props {
  product: Page;
  userData: User;
}

interface ContainerProps {
  productTag: string;
  userData: User;
}

interface ComponentsMapping {
  [key: string]: FunctionComponent<{ section: Section; tiles: Tile[] }>;
}

export const Product: FunctionComponent<Props> = ({ product, userData }) => {
  const intl = useIntl();

  useEffect(() => {
    document.title =
      product.metadata?.metaTitle || intl.formatMessage({ id: "cheerz_Home_meta_title" });
    setMeta(
      "description",
      product.metadata?.metaDescription ||
        intl.formatMessage({ id: "cheerz_Home_meta_description" })
    );
  }, []);

  const productComponentsList: ComponentsMapping = {
    medium_colored_icons_web: MediumColoredIcons,
    medium_product_cards_web: MediumProductCards,
    product_description_web: ProductDescription,
  };

  const ProductComponents = product.sections.map((section: Section) => {
    const Component = productComponentsList[section.displayType];
    const tiles = product.tiles.filter((tile) => new Set(section.tileSlugs).has(tile.slug));

    return Component ? (
      <Component key={section.displayType} section={section} tiles={tiles} />
    ) : null;
  });

  const imageListSection = product.sections.filter(
    (section) => section.displayType === "image_list_web"
  )[0];
  const imageListTiles = product.tiles.filter((tile) =>
    new Set(imageListSection.tileSlugs).has(tile.slug)
  );
  const productInfosSection = product.sections.filter(
    (section) => section.displayType === "product_infos_web"
  )[0];
  const productInfosTiles = product.tiles.filter((tile) =>
    new Set(productInfosSection.tileSlugs).has(tile.slug)
  );

  useEffect(() => {
    if (imageListTiles.length > 0) {
      addProductPageStructuredData(product, imageListTiles[0], productInfosTiles[0], userData);
    }
  }, []);

  const { isLargeScreen } = useBreakpoint();
  const bannerKountriesHeight = useBannerKountriesHeight();
  const marginTop = isLargeScreen ? HEADER_HEIGHT + bannerKountriesHeight : 0;

  return (
    <div className="Product" style={{ marginTop }}>
      <ProductInfos
        imageListTiles={imageListTiles}
        productInfosSection={productInfosSection}
        productInfosTiles={productInfosTiles}
      />
      {ProductComponents}
    </div>
  );
};

const ProductContainer: FunctionComponent<ContainerProps> = ({ productTag, userData }) => {
  const [product, setProduct] = useState<Page>();
  const [settings, setSettings] = useState<SettingsContent>();
  const { setLoading } = useGlobalLoader();
  const intl = useIntl();
  const locale = intl.locale;

  const productSlug = `product_page_web-${productTag}`;

  const cheerzApiProvider = new CheerzApiProvider(locale, getCsrfToken(), true);

  useEffect(() => {
    cheerzApiProvider
      .getSettings(locale)
      .then((settingsData) => {
        setSettings(convertSettingsFromApiToContent(settingsData.response));
        return cheerzApiProvider.getPage(productSlug, settingsData.response).then((data) => {
          setProduct(convertPageFromApiToContent(data.response));
          setLoading(false);
        });
      })
      .catch((error: ApiV2ErrorResponse) => apiV2ErrorManager(error));
  }, []);

  useEffect(() => {
    if (product && settings) {
      setDataLayerMandatoryPageTrackers(
        settings?.currency,
        settings?.lang,
        productTag,
        "Product",
        window.location.pathname,
        userData
      );
    }
  }, [product, settings]);

  return (
    <WithLocale history={history}>
      {product && <Product product={product} userData={userData} />}
    </WithLocale>
  );
};

export default ProductContainer;
